import { useMountEffect, type AsyncStatus } from "@react-hookz/web";
import type { Dispatch, SetStateAction } from "react";
import { useEffect } from "react";
import { useStore } from "zustand";

import { usePitchedContentsLoader } from "../../../../hooks/pitchedContent/usePitchedContentsLoader";
import { editContentStore } from "../../../../store/editContent";
import { isSuccess } from "../../../../utils/api.utils";
import { dateSort } from "../../../../utils/date.utils";
import { withInsightsRenderer } from "../withInsightsRenderer";

import { PitchedContentCard } from "./PitchedContentsCard";
import { SharePitchedContentModal } from "./SharePitchedContentModal";

interface PitchedContentsProps {
    isLoaderInView: boolean;
    setAsyncStatus: Dispatch<SetStateAction<AsyncStatus>>;
    setTotalCount: Dispatch<SetStateAction<number>>;
}

export const PitchedContents: React.FunctionComponent<PitchedContentsProps> = (
    props: PitchedContentsProps,
) => {
    const { isLoaderInView, setAsyncStatus, setTotalCount } = props;
    const [
        asyncStatus,
        lazyLoadedContents,
        totalResults,
        fetchPitchedContents,
    ] = usePitchedContentsLoader({
        isLoaderInView,
    });

    useEffect(() => {
        setAsyncStatus(asyncStatus);
    }, [asyncStatus]);

    useEffect(() => {
        if (isSuccess(asyncStatus)) {
            setTotalCount(totalResults);
        }
    }, [asyncStatus, totalResults]);

    const [setOnSubmitHandler] = useStore(editContentStore, (state) => [
        state.setOnSubmitHandler,
    ]);

    useMountEffect(() => {
        setOnSubmitHandler(fetchPitchedContents);
    });

    return (
        <>
            {lazyLoadedContents
                .sort(dateSort("lastModifiedDate"))
                .map((content, index) => (
                    <PitchedContentCard
                        key={`pitched-${index}-${content.contentId}`}
                        content={content}
                        isEditable={true}
                        isSharable={true}
                    />
                ))}
            <SharePitchedContentModal />
        </>
    );
};

export const PitchedContentsViewer = withInsightsRenderer(PitchedContents);
